import { inFireRing, TILE_SIZE, type TilePosition } from '@shefira/shared';
import type Phaser from 'phaser';

/**
 * Shefira's ring of fire (ADR 0068 §3, phase 3): every open tile further
 * than `safe` rings from her home burns when it lands. The server sends no
 * list (too many tiles): the client lays them from `safe` out to the rim,
 * marked in the warnings' red while it gathers, the safe square's edge
 * blinking faster as it nears, then a sheet of fire. Drawn by code in whole
 * pixels like every warning (CLAUDE.md rule 6); nothing here decides who
 * burns.
 */

/** The tiles the ring burns, out to `reach` rings: open ones only, the rock does not burn. */
export function fireRingTiles(
  centre: TilePosition,
  safe: number,
  reach: number,
  isOpen: (tile: TilePosition) => boolean,
): TilePosition[] {
  const tiles: TilePosition[] = [];
  for (let y = centre.y - reach; y <= centre.y + reach; y += 1) {
    for (let x = centre.x - reach; x <= centre.x + reach; x += 1) {
      const tile = { x, y };
      if (inFireRing(centre, safe, tile) && isOpen(tile)) tiles.push(tile);
    }
  }
  return tiles;
}

/** The safe square, in world pixels: the tiles `safe` rings round the centre, and no further. */
export function safeSquare(
  centre: TilePosition,
  safe: number,
): { x: number; y: number; size: number } {
  return {
    x: (centre.x - safe) * TILE_SIZE,
    y: (centre.y - safe) * TILE_SIZE,
    size: (safe * 2 + 1) * TILE_SIZE,
  };
}

/** With the other warnings, just over the ground. */
const DEPTH = -389;
const WARNING = 0xff3b3b;
const WARNING_DARK = 0x8a1a1a;
const WHITE = 0xf8f2ea;
const FIRE = [0xffe066, 0xff9a2a, 0xe0461a, 0x7a1a0a] as const;
const BURN_MS = 900;
/** Her arena is fifteen tiles each side: the rim is never further. */
const REACH = 17;

export class FireRing {
  private graphics?: Phaser.GameObjects.Graphics;
  private tween?: Phaser.Tweens.Tween;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly isOpen: (tile: TilePosition) => boolean,
  ) {}

  /** Marked: the ring gathers for `ms`. */
  mark(centre: TilePosition, safe: number, ms: number): void {
    this.clear();
    const tiles = fireRingTiles(centre, safe, REACH, this.isOpen);
    const square = safeSquare(centre, safe);
    const graphics = this.scene.add.graphics().setDepth(DEPTH);
    this.graphics = graphics;
    this.tween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: Math.max(1, ms),
      onUpdate: (counter) => {
        const p = counter.getValue() ?? 0;
        graphics.clear();
        graphics.fillStyle(WARNING_DARK, 0.25 + p * 0.25);
        for (const tile of tiles) {
          graphics.fillRect(tile.x * TILE_SIZE, tile.y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        }
        // Sparks crawl over the burning ground, thicker as it nears.
        const shift = Math.floor(p * 32);
        graphics.fillStyle(WARNING, 0.5);
        for (const tile of tiles) {
          if ((tile.x * 7 + tile.y * 13 + shift) % 5 !== 0) continue;
          graphics.fillRect(
            tile.x * TILE_SIZE + ((tile.y + shift) % 14) + 1,
            tile.y * TILE_SIZE + 7,
            2,
            2,
          );
        }
        const blinks = p < 0.5 ? 2 : p < 0.8 ? 4 : 8;
        const on = Math.floor(p * blinks * 2) % 2 === 0;
        // The safe square's edge, one whole pixel wide: where to stand.
        graphics.fillStyle(on ? WARNING : WHITE, 1);
        graphics.fillRect(square.x, square.y, square.size, 1);
        graphics.fillRect(square.x, square.y + square.size - 1, square.size, 1);
        graphics.fillRect(square.x, square.y, 1, square.size);
        graphics.fillRect(square.x + square.size - 1, square.y, 1, square.size);
      },
    });
  }

  /** Landed: a sheet of fire over the ring, gone in a moment. */
  land(centre: TilePosition, safe: number): void {
    this.clear();
    const tiles = fireRingTiles(centre, safe, REACH, this.isOpen);
    const graphics = this.scene.add.graphics().setDepth(DEPTH);
    this.graphics = graphics;
    this.tween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: BURN_MS,
      onUpdate: (counter) => {
        const p = counter.getValue() ?? 0;
        graphics.clear();
        const shade = FIRE[Math.min(FIRE.length - 1, Math.floor(p * FIRE.length))] ?? WARNING;
        graphics.fillStyle(shade, Math.max(0, 0.75 - p * 0.75));
        for (const tile of tiles) {
          graphics.fillRect(tile.x * TILE_SIZE, tile.y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        }
        graphics.fillStyle(FIRE[0], Math.max(0, 1 - p * 1.2));
        const rise = Math.round(p * 10);
        for (const tile of tiles) {
          if ((tile.x * 5 + tile.y * 3) % 3 !== 0) continue;
          graphics.fillRect(
            tile.x * TILE_SIZE + ((tile.x * 11) % 13) + 1,
            tile.y * TILE_SIZE + 12 - rise,
            1,
            3,
          );
        }
      },
      onComplete: () => {
        this.clear();
      },
    });
  }

  clear(): void {
    this.tween?.stop();
    this.tween = undefined;
    this.graphics?.destroy();
    this.graphics = undefined;
  }
}
